import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

const MISSED_CALLS_SEEN_KEY = "elefa:missed-calls-seen-at";

export function useMissedCallsCount() {
  const queryClient = useQueryClient();

  const { data: count = 0 } = useQuery({
    queryKey: ["missed-calls-count"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return 0;

      // Only count missed calls AFTER the last time the user viewed recents
      const seenAt = localStorage.getItem(MISSED_CALLS_SEEN_KEY);
      const since = new Date();
      since.setHours(since.getHours() - 24);

      // Use the later of: 24h ago OR last-seen timestamp
      const cutoff = seenAt && new Date(seenAt) > since ? seenAt : since.toISOString();

      const { count, error } = await supabase
        .from("call_history")
        .select("*", { count: "exact", head: true })
        .eq("user_id", session.user.id)
        .in("status", ["missed", "rejected", "failed", "no-answer"])
        .gte("started_at", cutoff)
        .is("deleted_at", null);

      if (error) {
        console.error("Failed to fetch missed calls count:", error);
        return 0;
      }
      return count || 0;
    },
    refetchInterval: 30000,
    staleTime: 15000,
  });

  const markMissedCallsSeen = useCallback(() => {
    localStorage.setItem(MISSED_CALLS_SEEN_KEY, new Date().toISOString());
    queryClient.invalidateQueries({ queryKey: ["missed-calls-count"] });
  }, [queryClient]);

  return { count, markMissedCallsSeen };
}
